import React from 'react'
import { Box, Paper, Typography, makeStyles } from '@material-ui/core'

const PICTOGRAM_LIMIT = 40

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(2),
    minWidth: 0,
    overflow: 'hidden'
  },
  title: { fontWeight: 700 },
  description: { color: '#66707a', marginBottom: theme.spacing(2) },
  result: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0)
  },
  heroStar: {
    color: '#9A6700',
    fontSize: '4.5rem',
    lineHeight: 1,
    textShadow: '0 2px 0 #F6D365'
  },
  qualified: { fontWeight: 800, lineHeight: 1 },
  total: { color: '#4D5963', marginTop: theme.spacing(0.5) },
  percentage: { color: '#7A5200', fontWeight: 700, marginTop: theme.spacing(0.5) },
  pictograms: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    minHeight: 42,
    margin: theme.spacing(2, 0)
  },
  pictogram: {
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.15rem'
  },
  star: { color: '#9A6700' },
  other: { color: '#66707a' },
  track: {
    height: 22,
    overflow: 'hidden',
    borderRadius: 11,
    background: '#DDE2E6',
    margin: theme.spacing(3, 0, 2)
  },
  fill: {
    height: '100%',
    minWidth: 0,
    borderRadius: 11,
    background: 'linear-gradient(90deg, #B77900, #F6C445)'
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(1, 3),
    color: '#4D5963'
  },
  legendSymbol: { display: 'inline-block', width: 20, textAlign: 'center', marginRight: theme.spacing(0.5) },
  empty: {
    minHeight: 58,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#66707a',
    margin: theme.spacing(2, 0)
  }
}))

export interface StarCasesData {
  qualified: number
  other: number
  total: number
  percentage: number
}

const safeCount = (value: number) => (Number.isFinite(value) && value > 0 ? Math.floor(value) : 0)

export const StarCasesChart: React.FC<{ data: StarCasesData }> = ({ data }) => {
  const classes = useStyles()
  const total = safeCount(data.total)
  const qualified = Math.min(safeCount(data.qualified), total)
  const other = total - qualified
  const percentage = total ? Math.round((qualified / total) * 1000) / 10 : 0

  return (
    <Paper className={classes.card} component="section" aria-label="Casos estrella">
      <Typography className={classes.title} variant="h6">
        Casos estrella
      </Typography>
      <Typography className={classes.description} variant="body2">
        Casos del mes con completo en oficina y una única cuota de honorarios pagada y confirmada.
      </Typography>

      <Box className={classes.result}>
        <span className={classes.heroStar} aria-hidden="true">
          ★
        </span>
        <Box>
          <Typography className={classes.qualified} variant="h3">
            {qualified}
          </Typography>
          <Typography className={classes.total}>de {total} casos del mes</Typography>
          <Typography className={classes.percentage}>{percentage.toFixed(1)}%</Typography>
        </Box>
      </Box>

      {!total ? (
        <Box className={classes.empty}>No hay casos para este período.</Box>
      ) : total <= PICTOGRAM_LIMIT ? (
        <Box
          className={classes.pictograms}
          aria-label={`${qualified} casos estrella y ${other} otros casos`}
          role="img"
        >
          {Array.from({ length: total }, (_, index) => (
            <span
              aria-hidden="true"
              className={`${classes.pictogram} ${index < qualified ? classes.star : classes.other}`}
              key={index}
            >
              {index < qualified ? '★' : '●'}
            </span>
          ))}
        </Box>
      ) : (
        <Box
          aria-label={`${qualified} casos estrella de ${total}; ${percentage.toFixed(1)} por ciento`}
          aria-valuemax={total}
          aria-valuemin={0}
          aria-valuenow={qualified}
          className={classes.track}
          role="progressbar"
        >
          <Box className={classes.fill} style={{ width: `${percentage}%` }} />
        </Box>
      )}

      <Box className={classes.legend} aria-label="Leyenda de casos estrella">
        <span>
          <span className={`${classes.legendSymbol} ${classes.star}`} aria-hidden="true">
            ★
          </span>
          Casos estrella: {qualified}
        </span>
        <span>
          <span className={`${classes.legendSymbol} ${classes.other}`} aria-hidden="true">
            ●
          </span>
          Otros casos: {other}
        </span>
      </Box>
    </Paper>
  )
}

export default StarCasesChart
